import { AfterViewInit, ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, Output, ViewChild } from "@angular/core";
import { IdentityDocumentValidationAttribute, OrderDetailIndividualDocumentView } from "../../..";
import { cloneDeep } from 'lodash';
import * as moment from 'moment';
import { NgForm } from "@angular/forms";
import { DocumentTypeSelectionComponent } from "src/app/core/components/document-type-selection/document-type-selection.component";
import { filter, takeUntil } from "rxjs/operators";
import { Subject, Subscription } from "rxjs";
import { CountrySelectionComponent } from "src/app/core/components/country-selection/country-selection.component";
import { InsightPassengerDocumentModel } from "src/app/core/models/individual-model";
import { DateConverterService } from "src/app/core/utils/date-converter.service";
import { DateTimeFormatService } from 'src/app/core/services/datetime-format.service';
import { OrganisationAttributeModel } from "src/app/core/models/organisation-model";
import { CountryReferenceService, IdentityDocumentReferenceService } from "src/app/core/services/system-services";
import { CountryReferenceModel, IdentityDocumentReferenceModel } from "src/app/core/models/reference-model/reference-general-model";

@Component({
    selector: 'op-order-detail-individual-document-form',
    templateUrl: './order-detail-individual-document-form.component.html'
})
export class OrderDetailIndividualDocumentFormComponent implements AfterViewInit, OnDestroy {

    @Input('data') set data(view: OrderDetailIndividualDocumentView) {
        this.view = cloneDeep(view);
    }
    
    @Input() individualId: string;
    @Input() organisationAttributes: OrganisationAttributeModel[];
    @Output() apply = new EventEmitter<OrderDetailIndividualDocumentView>();
    @Output() close = new EventEmitter<void>();
    @Output() undo = new EventEmitter();

    @ViewChild(NgForm) form: NgForm;
    @ViewChild(DocumentTypeSelectionComponent) documentTypeSelectionComponent: DocumentTypeSelectionComponent;
    @ViewChild(CountrySelectionComponent) countrySelectionComponent: CountrySelectionComponent;

    focused: boolean = false;
    processing: boolean = false;
    view: OrderDetailIndividualDocumentView;
    issueDateOption: any;
    expiryDateOption: any;
    
    public errorClass = "is-invalid";

    get countryError(): boolean {
        if (!this.countryRequired) return false;
        return this.processing && !(this.view?.countryCode);
    }

    get typeRequired(): boolean {
        return this.organisationAttributes
            ?.some(a => a.organisationAttributeTypeCode == IdentityDocumentValidationAttribute.Type);
    }

    get numberRequired(): boolean {
        return this.organisationAttributes
            ?.some(a => a.organisationAttributeTypeCode == IdentityDocumentValidationAttribute.Number);
    }

    get countryRequired(): boolean {
        return this.organisationAttributes
            ?.some(a => a.organisationAttributeTypeCode == IdentityDocumentValidationAttribute.Country);
    }

    get placeRequired(): boolean {
        return this.organisationAttributes
            ?.some(a => a.organisationAttributeTypeCode == IdentityDocumentValidationAttribute.Place);
    }

    get issueDateRequired(): boolean {
        return this.organisationAttributes
            ?.some(a => a.organisationAttributeTypeCode == IdentityDocumentValidationAttribute.IssueDate);
    }

    get expiryDateRequired(): boolean {
        return this.organisationAttributes
            ?.some(a => a.organisationAttributeTypeCode == IdentityDocumentValidationAttribute.ExpiryDate);
    }

    private readonly ISSUE_DATE_OPTION = {
        singleDatePicker: true,
        showDropdowns: true,
        autoApply: true,
        autoUpdateInput: false,
        startDate: moment(),
        endDate: moment(),
        maxDate: new Date(),
        locale: {
            cancelLabel: 'Clear',
            format: 'YYYY-MM-DD'
        },
        isCustomDate: null
    }
    
    private readonly EXPIRY_DATE_OPTION = {
        singleDatePicker: true,
        showDropdowns: true,
        autoApply: true,
        autoUpdateInput: false,
        startDate: moment(),
        endDate: moment(),
        locale: {
            cancelLabel: 'Clear',
            format: 'YYYY-MM-DD'
        },
        isCustomDate: null
    }

    private destroy$ = new Subject();
    private subscriptions: Subscription[]

    constructor(private changeDetector: ChangeDetectorRef,
        private dateConversion: DateConverterService,
        private dateTimeFormatService: DateTimeFormatService,
        private identityDocumentReferenceService: IdentityDocumentReferenceService,
        private countryReferenceService: CountryReferenceService
    ) {
        this.expiryDateOption = {
            displayFormat: this.dateTimeFormatService.getShortDateFormat(),
            inputFormat:  this.dateTimeFormatService.getInputDateFormat(),
            firstDayOfWeek: this.dateTimeFormatService.getFirstDayOfWeek()
        };

        this.issueDateOption = cloneDeep(this.expiryDateOption);
        this.issueDateOption.maxDate = new Date();
        this.subscriptions = [];
    }

    ngAfterViewInit(): void {
        this.checkIfErrorPresist();
        this.subscriptToIdentityDocumentTypeReference();
        this.subscriptToCountryReference();
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(s => s.unsubscribe())
        this.destroy$.next();
        this.destroy$.complete();
    }

    onUndoClick() {
        this.undo.emit();
    }
    onApplyClick() {
        this.processing = true;
        if (!this.form.valid || this.countryError) {
            return;
        }

        this.view.countryName = this.countrySelectionComponent.getSelectedText();
        this.view.identityDocumentName = this.documentTypeSelectionComponent.getSelectedText();
        this.apply.emit(this.view);
    }
    onClearClick() {
        this.resetInputFields();
    }
    onCloseClick() {
        this.close.emit();
    }
    private resetInputFields() {
        this.view = this.getEmptyView();
    }
    private getEmptyView(): OrderDetailIndividualDocumentView {
        return {
            identityDocumentId: null,
            identityDocumentCode: null,
            identityDocumentName: null,
            identityDocumentNumber: null,
            countryCode: null,
            countryName: null,
            expiryDate: null,
            lastName: null,
            firstName: null,
            title: null,
            allowedEdit: null,
            showDetail: null,
            individualId: null,
            orderId: null,
            partnerOrderId: null,
            issueDate: null,
            placeOfIssue: null,
            orderSalesStatusCode: null
        }
    }
    private checkIfErrorPresist() {
        this.form.valueChanges
            .pipe(
                filter(_ => this.processing == true),
                takeUntil(this.destroy$)
            )
            .subscribe(_ => this.changeDetector.markForCheck());
    }

    public onPresetSelected(item: InsightPassengerDocumentModel) {
        if (!item) return;
        this.view = {
            identityDocumentId: item.individualIdentityDocumentId,
            identityDocumentCode: item.identityDocumentCode,
            identityDocumentName: item.identityDocumentName,
            identityDocumentNumber: item.identityDocumentNumber,
            countryCode: item.countryCode,
            countryName: item.countryName,
            expiryDate: this.dateConversion.toDateFormatOrNull(item.expiryDate),
            lastName: null,
            firstName: null,
            title: null,
            allowedEdit: null,
            showDetail: null,
            individualId: item.individualId,
            orderId: null,
            partnerOrderId: null,
            issueDate: this.dateConversion.toDateFormatOrNull(item.issueDate),
            placeOfIssue: item.placeOfIssue,
            orderSalesStatusCode: null
        }
    }

    public getErrorClass(): string {
        if (this.processing && this.countryError) return this.errorClass;

        return '';
    }

    private subscriptToIdentityDocumentTypeReference() {
        this.subscriptions.push(
            this.identityDocumentReferenceService.identityDocumentTypeReference
                .subscribe(identityDocumentTypeReference => {
                    this.checkIdentityDocumentTypeReference(identityDocumentTypeReference);
                })
        )
    }

    private checkIdentityDocumentTypeReference(reference: IdentityDocumentReferenceModel[]) {
        if (!(reference?.length > 0)) return;
        if (!reference.find(item => item.identityDocumentCode == this.view?.identityDocumentCode)) {
            this.view.identityDocumentCode = null;
            this.view.identityDocumentName = null;
        }
    }

    private subscriptToCountryReference() {
        this.subscriptions.push(
            this.countryReferenceService.countryReference
                .subscribe(countryReference => {
                    this.checkCountryReference(countryReference);
                })
        )
    }

    private checkCountryReference(reference: CountryReferenceModel[]) {
        if (!(reference?.length > 0)) return;
        if (!reference.find(item => item.countryCode == this.view?.countryCode)) {
            this.view.countryCode = null;
            this.view.countryName = null;
        }
    }
}